import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaUserPlus } from "react-icons/fa";
import PasswordInput from "../auth/login/PasswordInput";
import PageHeader from "../../core/common/header/PageHeader";

const MentorRegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    whatsappNumber: "",
    password: "",
    confirmPassword: "",
  });

  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdMentorName, setCreatedMentorName] = useState("");

  const navigate = useNavigate();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  const handleRegister = async () => {
    const {
      firstName,
      lastName,
      email,
      whatsappNumber,
      password,
      confirmPassword,
    } = formData;

    if (
      !firstName ||
      !lastName ||
      !email ||
      !whatsappNumber ||
      !password ||
      !confirmPassword
    ) {
      setError("🚫 Please fill out all fields.");
      return;
    }

    if (password !== confirmPassword) {
      setError("🚫 Passwords do not match.");
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/register-mentor`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            firstName,
            lastName,
            email,
            whatsappNumber,
            password,
          }),
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Registration failed (${res.status})`);
      }

      // Success! Show modal
      setCreatedMentorName(`${firstName} ${lastName}`);
      setError("");
      setShowSuccessModal(true);
    } catch (err: any) {
      console.error(err);
      setError(err.message);
      setIsSubmitting(false);
    }
  };

  const handleBackToManage = () => {
    setShowSuccessModal(false);
    navigate("/admin/manage-mentors");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mb-4 text-6xl">🎉</div>
              <h2 className="text-2xl font-bold mb-4 text-green-600">
                Mentor Created Successfully!
              </h2>
              <p className="mb-6 text-gray-700">
                <strong>{createdMentorName}</strong> has been successfully
                registered as a mentor. They can now log in and start guiding
                students.
              </p>
              <div className="flex gap-3">
                <button
                  className="flex-1 bg-[#009fe3] hover:bg-[#007cb5] text-white px-6 py-3 rounded font-semibold transition"
                  onClick={handleBackToManage}
                >
                  Back to Manage
                </button>
                <button
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded font-semibold transition"
                  onClick={() => {
                    setShowSuccessModal(false);
                    setFormData({
                      firstName: "",
                      lastName: "",
                      email: "",
                      whatsappNumber: "",
                      password: "",
                      confirmPassword: "",
                    });
                    setIsSubmitting(false);
                  }}
                >
                  Add Another
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PageHeader
        icon={<FaUserPlus />}
        title="Mentor Registration"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Mentors", to: "/admin/manage-mentors" },
          { label: "Register Mentor" },
        ]}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-3xl font-bold text-center text-[#009fe3] mb-2">
            Create New Mentor Account
          </h2>
          <p className="text-center text-gray-600 mb-6">
            Fill in the details to create a new mentor
          </p>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-6 text-center text-sm font-semibold">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="firstName"
                  className="block mb-1 font-medium text-gray-700"
                >
                  First Name *
                </label>
                <input
                  id="firstName"
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="block mb-1 font-medium text-gray-700"
                >
                  Last Name *
                </label>
                <input
                  id="lastName"
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block mb-1 font-medium text-gray-700"
              >
                Email Address *
              </label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label
                htmlFor="whatsappNumber"
                className="block mb-1 font-medium text-gray-700"
              >
                WhatsApp Number *
              </label>
              <input
                id="whatsappNumber"
                type="text"
                name="whatsappNumber"
                placeholder="WhatsApp Number"
                value={formData.whatsappNumber}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                label="Password"
                showStrengthIndicator={true}
                disabled={isSubmitting}
              />
              <br />
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                label="Confirm Password"
                showStrengthIndicator={false}
                disabled={isSubmitting}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={handleRegister}
              disabled={isSubmitting}
              className="flex-1 bg-[#009fe3] hover:bg-[#007cb5] text-white font-bold py-3 rounded transition disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Creating..." : "Create Mentor Account"}
            </button>
            <Link
              to="/admin/manage-mentors"
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded transition text-center flex items-center"
            >
              Cancel
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MentorRegisterPage;
